import { authLogger } from "../../utils/logger";
import { authService } from './authService';
import { sessionService } from './sessionService';
import { registrationService } from './registrationService';
import { 
  LoginResponse, 
  User, 
  ForgotPasswordResponse, 
  RegisterResponse, 
} from '../../types';
import { checkForRecentUserNotConfirmedException } from '../../utils/networkMonitor';
import { isUserNotConfirmedException, createUserNotConfirmedError } from '../../utils/cognitoErrorUtils';

// Helper function to check if we're in mock mode
// Extend Window interface for test overrides
declare global {
  interface Window {
    __TEST_MOCK_MODE__?: boolean;
  }
}

// Dynamic mock mode checker (evaluated at runtime)
const getMockMode = (): boolean => {
  if (typeof window !== 'undefined') {
    if (typeof window.__TEST_MOCK_MODE__ !== 'undefined') {
      return window.__TEST_MOCK_MODE__;
    }
    
    try {
      const testMode = localStorage.getItem('TEST_MOCK_MODE');
      if (testMode !== null) {
        return testMode === 'true';
      }
    } catch (e) {
      // localStorage might not be available
    }
    
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const mockParam = urlParams.get('mock');
      if (mockParam !== null) {
        return mockParam === 'true';
      }
    } catch (e) {
      // URL parsing might fail
    }
  }
  
  return import.meta.env.VITE_MOCK_MODE === 'true';
};

// Add the test mock mode property to the Window interface
declare global {
  interface Window {
    __TEST_MOCK_MODE__?: boolean;
  }
}

// Mock user for demo purposes
const mockLoginResponse: LoginResponse = {
  success: true,
  user: {
    id: 'mock-user-id',
    name: 'Demo User',
    email: 'demo@fabrik28.ai',
    firstName: 'Demo',
    lastName: 'User',
    initials: 'DU',
    company: 'Demo Company',
    role: 'Admin',
    tenantId: 'demo-tenant',
    refreshToken: 'mock-refresh-token'
  },
  tenantId: 'demo-tenant',
  tenantName: 'Demo Company',
  token: "mock-jwt-token",
  refreshToken: "mock-refresh-token"
};

// Auth API that matches the expected interface
export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    if (getMockMode()) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock validation
      if (email === 'demo@fabrik28.ai' && password === 'demo123') {
        return mockLoginResponse;
      } else {
        // Check if this looks like a real email/password combination that might be unverified
        // This simulates the scenario where user created account but didn't verify email
        if (email.includes('@') && password.length >= 8) {
          throw new Error('UserNotConfirmedException: User is not confirmed. Please verify your email address.');
        } else {
          throw new Error('Invalid credentials');
        }
      }
    }

    try {
      // First check if there's already a signed-in user
      const existingSession = await sessionService.checkExistingSession();
      
      if (existingSession.isValid) {
        // If the existing user is the same as the one trying to log in, return success
        if (existingSession.user?.email === email) {
          return {
            success: true,
            user: existingSession.user,
            token: existingSession.token || '',
            refreshToken: existingSession.user?.refreshToken || '',
            tenantId: existingSession.user.tenantId || '',
            tenantName: existingSession.user.company || ''
          };
        } else {
          // Different user is trying to log in, sign out the current user first
          await sessionService.signOut();
        }
      }

      // Proceed with new sign in
      const result = await authService.signIn(email, password);
      
      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // User needs to set a new password
        return {
          success: false,
          challengeName: 'NEW_PASSWORD_REQUIRED',
          username: result.username || email,
          user: getEmptyUser(),
          token: '',
          tenantId: '',
          tenantName: ''
        };
      } else if (result.isSignedIn) {
        // Get user details with attributes
        const user = await authService.getCurrentUser();
        const session = await authService.getCurrentSession();
        
        // Extract user attributes based on your Cognito setup
        const attributes = user?.attributes || {};
        
        const firstName = attributes.given_name || '';
        const lastName = attributes.family_name || '';
        const userEmail = attributes.email || user?.username || email;
        const tenantId = attributes['custom:tenantId'] || '';
        
        // Create full name
        const fullName = (firstName && lastName) ? `${firstName} ${lastName}` : '';
        const displayName = fullName || user?.username || userEmail;
        
        // Generate initials from first and last name
        const getInitials = (): string => {
          if (firstName && lastName) {
            const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
            return initials;
          } else if (fullName) {
            const nameParts = fullName.split(' ').filter(part => part.length > 0);
            if (nameParts.length >= 2) {
              const initials = `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
              return initials;
            } else if (nameParts.length === 1) {
              return nameParts[0].charAt(0).toUpperCase();
            }
          }
          return userEmail.charAt(0).toUpperCase();
        };
        
        // Check if first and last name are available and not default values
        const hasRealName = firstName && lastName && firstName !== 'N/A' && lastName !== 'N/A' && firstName !== 'Unavailable' && lastName !== 'Unavailable';
        
        // Update display name from email if first and last name are not available or are default values
        let updatedDisplayName = displayName;
        if (!hasRealName) {
          updatedDisplayName = userEmail.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
        
        // Generate initials from display name or set to empty string to use icon
        const userInitials = hasRealName 
          ? updatedDisplayName
              .split(' ')
              .map(part => part.charAt(0))
              .join('')
              .toUpperCase()
              .slice(0, 2)
          : ''; // Empty string indicates to use icon instead of initials
        
        // Determine user role based on given_name and family_name
        // If both are "N/A", assign "User" role, otherwise assign "Admin" role
        const userRole = (firstName === 'N/A' && lastName === 'N/A') ? 'User' : 'Admin';
        
        // Get tokens
        const accessToken = session?.tokens?.idToken?.toString() || '';
        const refreshToken = session?.tokens?.refreshToken?.toString() || '';
        
        // Create final user data
        const finalUserData: User = {
          id: user?.userId || '',
          name: hasRealName ? updatedDisplayName : 'Unknown', // Set name to Unknown when names are default values
          email: userEmail,
          firstName: firstName,
          lastName: lastName,
          initials: userInitials,
          useIcon: !hasRealName, // Add flag to indicate whether to use icon instead of initials
          company: 'N/A', // Set company to N/A by default
          role: userRole, // Role based on name values: N/A,N/A = User, otherwise = Admin
          tenantId: tenantId,
          refreshToken: refreshToken
        };
        
        return {
          success: true,
          user: finalUserData,
          token: accessToken,
          refreshToken: refreshToken,
          tenantId: tenantId,
        };
      } else {
        throw new Error('Authentication failed');
      }
    } catch (error) {
      authLogger.error('Login error details:', error);
      
      // FIRST: Check if there was a recent UserNotConfirmedException at the network level
      // This is the key fix - check the network monitor for the actual server response
      const recentCognitoError = checkForRecentUserNotConfirmedException(5000); // within last 5 seconds
      if (recentCognitoError && recentCognitoError.__type === 'UserNotConfirmedException') {
        throw createUserNotConfirmedError(email);
      }
      
      authLogger.error('Error object:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        name: error instanceof Error ? error.name : 'Unknown',
        stack: error instanceof Error ? error.stack : 'No stack',
        fullError: error,
        errorString: String(error),
        errorJSON: JSON.stringify(error, null, 2)
      });
      
      // Log the exact error message for debugging CloudFront vs local differences
      const errorMessage = error instanceof Error ? error.message : String(error);
      authLogger.error('Detailed error analysis:', {
        errorMessage,
        includesUserNotConfirmed: errorMessage.includes('User is not confirmed'),
        includesUserNotConfirmedException: errorMessage.includes('UserNotConfirmedException'),
        errorToString: error.toString(),
        errorType: typeof error,
        isError: error instanceof Error
      });
      
      // Use the utility function to check for UserNotConfirmedException
      const isUnconfirmed = isUserNotConfirmedException(error);
      authLogger.error('UserNotConfirmedException check result:', { isUnconfirmed });
      
      if (isUnconfirmed) {
        authLogger.info('UserNotConfirmedException detected, user needs email verification', { email });
        throw createUserNotConfirmedError(email);
      }
      
      // Additional fallback check for the exact message you're seeing
      if (errorMessage === 'User is not confirmed.') {
        authLogger.info('Fallback detection: Found exact "User is not confirmed." message', { email });
        throw createUserNotConfirmedError(email);
      }
      
      // Handle other errors based on message content
      if (errorMessage.includes('Incorrect username or password')) {
        throw new Error('Invalid credentials. Please check your email and password.');
      } else if (errorMessage.includes('User does not exist')) {
        throw new Error('User does not exist. Please check your email or sign up.');
      } else {
        throw new Error('Authentication failed. Please try again.');
      }
    }
  },
  
  async checkSession(): Promise<LoginResponse | null> {
    try {
      const session = await sessionService.checkExistingSession();
      
      if (session.isValid && session.user) {
        return {
          success: true,
          user: session.user,
          token: session.token || '',
          refreshToken: session.user?.refreshToken || '',
          tenantId: session.user.tenantId || '',
          tenantName: session.user.company || ''
        };
      }
      
      return null;
    } catch (error) {
      authLogger.error('Error checking session:', error);
      return null;
    }
  },
  
  async logout(): Promise<void> {
    try {
      await sessionService.signOut();
    } catch (error) {
      authLogger.error('Error during logout:', error);
      throw error;
    }
  },

  /**
   * Register a new user with email and password
   */
  async register(userData: { email: string; password: string }): Promise<RegisterResponse> {
    if (getMockMode()) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock validation
      if (!userData.email || !userData.password) {
        throw new Error('Email and password are required');
      }
      
      if (userData.email === 'existing@fabrik28.ai') {
        throw new Error('User already exists');
      }
      
      // Mock successful registration
      return {
        success: true,
        message: 'Registration successful! Welcome to Fabrik 28.',
        user: {
          id: `user-${Date.now()}`,
          email: userData.email
        }
      };
    }

    try {
      // Use registrationService to sign up the user
      const signupData = {
        email: userData.email,
        password: userData.password,
        given_name: 'N/A',
        family_name: 'N/A',
        'custom:tenantId': `tenant-${Date.now()}`, // Generate a tenant ID
        'custom:subscriptionTier': 'FREE' // Default subscription tier
      };
      
      const result = await registrationService.signupUser(signupData);
      
      authLogger.info('User registration successful', { email: userData.email });
      
      return {
        success: true,
        message: result.message || 'Registration successful! Please check your email to verify your account.',
        user: {
          id: result.tenantId || `user-${Date.now()}`,
          email: userData.email
        }
      };
    } catch (error) {
      authLogger.error('Registration failed', { email: userData.email, error: error });
      throw error;
    }
  },


  /**
   * Initiate forgot password flow - sends verification code to user's email
   */
  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    if (getMockMode()) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock response
      return {
        success: true,
        message: 'Password reset code sent to your email',
        destination: email.replace(/(.{2})(.*)(@.*)/, '$1***$3') // Mask email
      };
    }

    try {
      const response = await authService.forgotPassword(email);
      return {
        success: true,
        message: 'Password reset code sent to your email',
        destination: response.destination
      };
    } catch (error) {
      authLogger.error('Error sending password reset code:', error);
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      return {
        success: false,
        message: errorMessage || 'Failed to send password reset code'
      };
    }
  },

  /**
   * Complete password reset with verification code and new password
   */
  async resetPassword(email: string, code: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    if (getMockMode()) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock validation - accept any 6-digit code
      if (code.length === 6) {
        return {
          success: true,
          message: 'Password reset successfully'
        };
      } else {
        return {
          success: false,
          message: 'Invalid verification code'
        };
      }
    }

    try {
      await authService.confirmForgotPassword(email, code, newPassword);
      
      return {
        success: true,
        message: 'Password reset successfully'
      };
    } catch (error) {
      authLogger.error('Error resetting password:', error);
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      return {
        success: false,
        message: errorMessage || 'Failed to reset password'
      };
    }
  },

  /**
   * Complete new password challenge for admin-created users
   */
  async completeNewPasswordChallenge(username: string, temporaryPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      if (getMockMode()) {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        return {
          success: true,
          message: 'Password set successfully'
        };
      }
      
      await authService.completeNewPasswordChallenge(username, temporaryPassword, newPassword);
      
      return {
        success: true,
        message: 'Password set successfully'
      };
    } catch (error) {
      authLogger.error('Error completing new password challenge:', error);
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      return {
        success: false,
        message: errorMessage || 'Failed to set new password'
      };
    }
  }
};

// Helper function to get an empty user object
function getEmptyUser(): User {
  return {
    id: '',
    name: '',
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    role: ''
  };
}
